import React from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { Box, CircularProgress, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import DashboardIcon from "@mui/icons-material/SpaceDashboardOutlined";
import HomeIcon from "@mui/icons-material/HomeOutlined";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import LightbulbIcon from "@mui/icons-material/LightbulbOutlined";
import RouterIcon from "@mui/icons-material/RouterOutlined";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import SystemUpdateIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import GroupIcon from "@mui/icons-material/GroupOutlined";
import ContactsIcon from "@mui/icons-material/ContactsOutlined";
import theme from "./theme";
import { PRODUCTS, productByPath } from "./config";
import { AuthProvider, useSession } from "./auth/AuthProvider";
import AppShell from "./components/AppShell";
import DeviceUpdatesPage from "./components/DeviceUpdatesPage";
import LoginPage, { NoAccessPage } from "./pages/LoginPage";
import { AdminDataProvider } from "./pages/admin/AdminData";
import OverviewPage from "./pages/admin/OverviewPage";
import LicensesPage from "./pages/admin/LicensesPage";
import DistributorsPage from "./pages/admin/DistributorsPage";
import DistributorDetailPage from "./pages/admin/DistributorDetailPage";
import TeamPage from "./pages/admin/TeamPage";
import AdminCustomersPage from "./pages/admin/CustomersPage";
import {
  DistributorDataProvider,
  useDistributorData,
} from "./pages/distributor/DistributorData";
import DistributorHome from "./pages/distributor/DistributorHome";
import MyKeysPage from "./pages/distributor/MyKeysPage";
import CustomersPage from "./pages/distributor/CustomersPage";

const PRODUCT_ICONS = { controller: <RouterIcon />, joy: <LightbulbIcon /> };

const ADMIN_NAV = [
  {
    items: [{ to: "/", label: "Overview", icon: <DashboardIcon />, end: true }],
  },
  {
    section: "Licences",
    items: PRODUCTS.map((p) => ({
      to: `/licenses/${p.path}`,
      label: p.name,
      icon: PRODUCT_ICONS[p.path],
    })),
  },
  {
    section: "Partners",
    items: [
      { to: "/distributors", label: "Distributors", icon: <StorefrontIcon /> },
      { to: "/customers", label: "Customers", icon: <ContactsIcon /> },
    ],
  },
  {
    section: "System",
    items: [
      { to: "/devices", label: "Device updates", icon: <SystemUpdateIcon /> },
      { to: "/team", label: "Team", icon: <GroupIcon /> },
    ],
  },
];

const DISTRIBUTOR_NAV = [
  {
    items: [
      { to: "/", label: "Home", icon: <HomeIcon />, end: true },
      { to: "/keys", label: "My keys", icon: <KeyIcon /> },
      { to: "/customers", label: "Customers", icon: <ContactsIcon /> },
    ],
  },
];

function LicensesRoute() {
  const { productPath } = useParams();
  const product = productByPath(productPath);
  if (!product) return <Navigate to="/" replace />;
  // key: reset filters when switching product
  return <LicensesPage key={product.id} product={product} />;
}

function AdminApp() {
  return (
    <AdminDataProvider>
      <Routes>
        <Route element={<AppShell nav={ADMIN_NAV} subtitle="LiFE admin" />}>
          <Route index element={<OverviewPage />} />
          <Route path="licenses/:productPath" element={<LicensesRoute />} />
          <Route path="distributors" element={<DistributorsPage />} />
          <Route
            path="distributors/:distributorId"
            element={<DistributorDetailPage />}
          />
          <Route path="customers" element={<AdminCustomersPage />} />
          <Route path="devices" element={<DeviceUpdatesPage />} />
          <Route path="team" element={<TeamPage />} />
          {/* Links from the previous portal */}
          <Route
            path="dashboard"
            element={<Navigate to="/licenses/controller" replace />}
          />
          <Route
            path="joy-licenses"
            element={<Navigate to="/licenses/joy" replace />}
          />
          <Route
            path="device-updates"
            element={<Navigate to="/devices" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AdminDataProvider>
  );
}

function DistributorShell() {
  const { distributor } = useDistributorData();
  return (
    <AppShell
      nav={DISTRIBUTOR_NAV}
      subtitle={distributor?.name || "Distributor portal"}
    />
  );
}

function DistributorApp() {
  return (
    <DistributorDataProvider>
      <Routes>
        <Route element={<DistributorShell />}>
          <Route index element={<DistributorHome />} />
          <Route path="keys" element={<MyKeysPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </DistributorDataProvider>
  );
}

function Root() {
  const session = useSession();
  if (session.status === "loading") {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (session.status === "signedOut") return <LoginPage />;
  if (session.status !== "ready") return <NoAccessPage />;
  return session.role === "admin" ? <AdminApp /> : <DistributorApp />;
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Root />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
